import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { regenerateOpener } from '../services/openerGenerator';
import { getCurrentSessionId } from '../services/sessionService';
import { extractDomain } from '../services/websiteAnalyzer';
import * as FiIcons from 'react-icons/fi';
import InstantlyModal from './InstantlyModal';

const { FiDownload, FiRefreshCw, FiCheck, FiCopy, FiSearch, FiFilter, FiExternalLink, FiInfo, FiAlertTriangle, FiMail, FiSend, FiChevronDown, FiChevronUp } = FiIcons;

// Debug component to show scraped data
const DebugDataSection = ({ result }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const scrapingOptions = result.scrapingOptions || {};
  
  return (
    <div className="debug-section">
      <button 
        className="debug-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} />
        <span>Show Scraped Data</span>
      </button>
      
      {isExpanded && (
        <div className="debug-content">
          {/* Scraping Status Overview */}
          <div className="debug-section-item">
            <h4>Scraping Status</h4>
            <div className="debug-data" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <span style={{ 
                padding: '4px 8px', 
                borderRadius: '4px', 
                fontSize: '12px', 
                backgroundColor: scrapingOptions.scrapeWebsite ? (result.title ? '#d4edda' : '#f8d7da') : '#e2e3e5',
                color: scrapingOptions.scrapeWebsite ? (result.title ? '#155724' : '#721c24') : '#6c757d'
              }}>
                Website: {scrapingOptions.scrapeWebsite ? (result.title ? '✓ Success' : '✗ Failed') : 'Disabled'}
              </span>
              <span style={{ 
                padding: '4px 8px', 
                borderRadius: '4px', 
                fontSize: '12px', 
                backgroundColor: scrapingOptions.scrapeLinkedIn ? (result.linkedInData && (result.linkedInData.headline || result.linkedInData.name || result.linkedInData.summary || result.linkedInData.experience) ? '#d4edda' : '#f8d7da') : '#e2e3e5',
                color: scrapingOptions.scrapeLinkedIn ? (result.linkedInData && (result.linkedInData.headline || result.linkedInData.name || result.linkedInData.summary || result.linkedInData.experience) ? '#155724' : '#721c24') : '#6c757d'
              }}>
                LinkedIn: {scrapingOptions.scrapeLinkedIn ? (result.linkedInData && (result.linkedInData.headline || result.linkedInData.name || result.linkedInData.summary || result.linkedInData.experience) ? '✓ Success' : '✗ Failed') : 'Disabled'}
              </span>
              <span style={{ 
                padding: '4px 8px', 
                borderRadius: '4px', 
                fontSize: '12px', 
                backgroundColor: scrapingOptions.scrapeTalkingPoints ? (result.talkingPointsData?.websiteName ? '#d4edda' : '#f8d7da') : '#e2e3e5',
                color: scrapingOptions.scrapeTalkingPoints ? (result.talkingPointsData?.websiteName ? '#155724' : '#721c24') : '#6c757d'
              }}>
                Talking Points: {scrapingOptions.scrapeTalkingPoints ? (result.talkingPointsData?.websiteName ? '✓ Success' : '✗ Failed') : 'Disabled'}
              </span>
            </div>
          </div>
          
          <div className="debug-section-item">
            <h4>Website Data {result.title ? '✓' : '✗'}</h4>
            <div className="debug-data">
              {result.title ? (
                <div>
                  <p><strong>Title:</strong> {result.title}</p>
                  <p><strong>Content Length:</strong> {result.content?.length || 0} characters</p>
                  {result.content && <p><strong>Content Preview:</strong> {result.content.substring(0, 200)}...</p>}
                  {result.source && <p><strong>Source:</strong> {result.source}</p>}
                </div>
              ) : (
                <p style={{ color: '#721c24' }}>No website data available</p>
              )}
            </div>
          </div>
          
          <div className="debug-section-item">
            <h4>LinkedIn Data {result.linkedInData && (result.linkedInData.headline || result.linkedInData.name || result.linkedInData.summary || result.linkedInData.experience) ? '✓' : '✗'}</h4>
            <div className="debug-data">
              {result.linkedInData && (result.linkedInData.headline || result.linkedInData.name || result.linkedInData.summary || result.linkedInData.experience) ? (
                <div>
                  {result.linkedInData.name && <p><strong>Name:</strong> {result.linkedInData.name}</p>}
                  {result.linkedInData.headline && <p><strong>Headline:</strong> {result.linkedInData.headline}</p>}
                  {result.linkedInData.company && <p><strong>Company:</strong> {result.linkedInData.company}</p>}
                  {result.linkedInData.summary && <p><strong>Summary:</strong> {result.linkedInData.summary.substring(0, 200)}...</p>}
                  {result.linkedInData.experience && <p><strong>Experience:</strong> {typeof result.linkedInData.experience === 'string' ? result.linkedInData.experience.substring(0, 150) + '...' : 'Available'}</p>}
                  {result.linkedInData.education && <p><strong>Education:</strong> {typeof result.linkedInData.education === 'string' ? result.linkedInData.education.substring(0, 150) + '...' : 'Available'}</p>}
                  {result.linkedInData.highlights && <p><strong>Highlights:</strong> {typeof result.linkedInData.highlights === 'string' ? result.linkedInData.highlights.substring(0, 150) + '...' : 'Available'}</p>}
                </div>
              ) : (
                <p style={{ color: scrapingOptions.scrapeLinkedIn ? '#721c24' : '#6c757d' }}>
                  {scrapingOptions.scrapeLinkedIn ? 'LinkedIn scraping failed or no data returned' : 'LinkedIn scraping disabled'}
                </p>
              )}
            </div>
          </div>
          
          <div className="debug-section-item">
            <h4>Talking Points Data {result.talkingPointsData?.websiteName ? '✓' : '✗'}</h4>
            <div className="debug-data">
              {result.talkingPointsData?.websiteName ? (
                <div>
                  <p><strong>Website:</strong> {result.talkingPointsData.websiteName}</p>
                  {result.talkingPointsData.websiteSummary && <p><strong>Summary:</strong> {result.talkingPointsData.websiteSummary.substring(0, 200)}...</p>}
                  {result.talkingPointsData.opportunities && result.talkingPointsData.opportunities.length > 0 && (
                    <p><strong>Opportunities ({result.talkingPointsData.opportunities.length}):</strong> {result.talkingPointsData.opportunities.slice(0, 3).join(', ')}</p>
                  )}
                </div>
              ) : (
                <p style={{ color: scrapingOptions.scrapeTalkingPoints ? '#721c24' : '#6c757d' }}>
                  {scrapingOptions.scrapeTalkingPoints ? 'Talking points scraping failed or no data returned' : 'Talking points scraping disabled'}
                </p>
              )}
            </div>
          </div>
          
          <div className="debug-section-item">
            <h4>Qualification Analysis {result.fitScore >= 7 ? '✓' : result.fitScore ? '✗' : '⏳'}</h4>
            <div className="debug-data">
              {result.fitScore !== undefined ? (
                <div>
                  <div style={{ marginBottom: '10px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: result.fitScore >= 7 ? '#d4edda' : '#f8d7da',
                      color: result.fitScore >= 7 ? '#155724' : '#721c24'
                    }}>
                      {result.fitScore >= 7 ? '✓ Qualified' : '✗ Not Qualified'}
                    </span>
                    {result.confidence && (
                      <span style={{
                        marginLeft: '10px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0'
                      }}>
                        {(() => {
                          const confidenceMap = {
                            'high': 90,
                            'medium': 70,
                            'low': 50
                          };
                          const numericConfidence = typeof result.confidence === 'number' 
                            ? result.confidence * 100 
                            : confidenceMap[result.confidence] || 70;
                          return Math.round(numericConfidence);
                        })()}% confidence
                      </span>
                    )}
                  </div>
                  {result.fitScore && <p><strong>Fit Score:</strong> {result.fitScore}/10</p>}
                  {(result.qualificationReasoning || result.reasoning) && (
                    <div style={{ marginTop: '10px' }}>
                      <p><strong>Analysis Summary:</strong></p>
                      <p style={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: '8px', 
                        borderRadius: '4px', 
                        fontSize: '13px',
                        lineHeight: '1.4',
                        marginTop: '5px'
                      }}>
                        {result.qualificationReasoning || result.reasoning}
                      </p>
                    </div>
                  )}
                  {result.keyIndicators && result.keyIndicators.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <p><strong>Key Indicators:</strong></p>
                      <ul style={{ 
                        margin: '5px 0 0 0', 
                        paddingLeft: '20px',
                        fontSize: '13px',
                        lineHeight: '1.4'
                      }}>
                        {result.keyIndicators.map((indicator, index) => (
                          <li key={index} style={{ marginBottom: '3px' }}>{indicator}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {result.potential_value && (
                    <div style={{ marginTop: '10px' }}>
                      <p><strong>Potential Value:</strong></p>
                      <p style={{ 
                        backgroundColor: '#f0f9ff', 
                        padding: '8px', 
                        borderRadius: '4px', 
                        fontSize: '13px',
                        lineHeight: '1.4',
                        marginTop: '5px',
                        color: '#0369a1'
                      }}>
                        {result.potential_value}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p style={{ color: '#6c757d' }}>No qualification analysis available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PersonalizedOpenerGenerator = ({ results, config, openerConfig }) => {
  const [selectedWebsites, setSelectedWebsites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState('Loading...');
  const [regenerating, setRegenerating] = useState({});
  const [localResults, setLocalResults] = useState(results);
  const [copiedState, setCopiedState] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [showInstantlyModal, setShowInstantlyModal] = useState(false);
  // No longer need useSequential state as we'll use openerConfig.strategy instead

  // Function to refresh results from localStorage
  const refreshResultsFromStorage = () => {
    const sessionId = getCurrentSessionId();
    console.log('PersonalizedOpenerGenerator: Refreshing from storage, sessionId:', sessionId);
    if (sessionId) {
      const sessionData = localStorage.getItem(`session_${sessionId}_data`);
      console.log('PersonalizedOpenerGenerator: Session data exists:', !!sessionData);
      if (sessionData) {
        try {
          const parsedData = JSON.parse(sessionData);
          console.log('PersonalizedOpenerGenerator: Parsed data:', parsedData);
          if (parsedData.results) {
            console.log('PersonalizedOpenerGenerator: Setting results from localStorage:', parsedData.results.length, 'items');
            setLocalResults(parsedData.results);
          } else {
            console.log('PersonalizedOpenerGenerator: No results in parsed data');
          }
        } catch (error) {
          console.error('PersonalizedOpenerGenerator: Error parsing session data:', error);
        }
      } else {
        console.log('PersonalizedOpenerGenerator: No session data found in localStorage');
      }
    } else {
      console.log('PersonalizedOpenerGenerator: No session ID available');
    }
  };

  // Initialize local results from props or localStorage (only on mount)
  useEffect(() => {
    console.log('PersonalizedOpenerGenerator: Initializing with results:', results);
    const sessionId = getCurrentSessionId();
    const sessionData = localStorage.getItem(`session_${sessionId}_data`);
    const openerData = localStorage.getItem(`session_${sessionId}_openers`);
    
    setDebugInfo(`Session: ${sessionId || 'None'} | Session Data: ${!!sessionData} | Opener Data: ${!!openerData} | Props Results: ${results?.length || 0}`);
    
    if (results && results.length > 0) {
      console.log('PersonalizedOpenerGenerator: Setting results from props:', results.length, 'items');
      setLocalResults(results);
      setDebugInfo(prev => prev + ` | Using Props: ${results.length} items`);
    } else {
      console.log('PersonalizedOpenerGenerator: No results from props, trying localStorage');
      // Try to load from localStorage if no results provided
      refreshResultsFromStorage();
    }
  }, []); // Only run on mount, don't reset when results prop changes

  // Track if this is the initial load to prevent unwanted regeneration
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [previousServiceType, setPreviousServiceType] = useState(null);

  // Mark initial load as complete after first render
  useEffect(() => {
    if (openerConfig?.serviceType && isInitialLoad) {
      setPreviousServiceType(openerConfig.serviceType);
      setIsInitialLoad(false);
    }
  }, [openerConfig?.serviceType, isInitialLoad]);

  // Regenerate openers when service type changes (but not on initial load)
  useEffect(() => {
    if (!openerConfig || !localResults.length || !config?.apiKey || isInitialLoad) return;
    
    const currentServiceType = openerConfig.serviceType;
    const currentSubServiceType = openerConfig.subServiceType;
    
    // Only regenerate if the service type actually changed from a previous value
    if (previousServiceType && previousServiceType !== currentServiceType) {
      console.log('Service type changed from', previousServiceType, 'to', currentServiceType, '- regenerating openers');
      
      // Check if any results need regeneration due to service type change
      const needsRegeneration = localResults.some(result => 
        result.serviceType !== currentServiceType || 
        result.subServiceType !== currentSubServiceType
      );
      
      if (needsRegeneration) {
        // Regenerate openers for all results that don't match the current service type
        localResults.forEach(async (result) => {
          if (result.serviceType !== currentServiceType || result.subServiceType !== currentSubServiceType) {
            // Only regenerate if the result has a fit score above threshold
            const fitScoreThreshold = config?.fitScoreThreshold || 6;
            const currentFitScore = result.fitScore || 0;
            
            if (currentFitScore >= fitScoreThreshold) {
              try {
                await handleRegenerateOpener(result);
              } catch (error) {
                console.error('Error regenerating opener for service type change:', error);
              }
            }
          }
        });
      }
    }
    
    // Update previous service type for next comparison
    setPreviousServiceType(currentServiceType);
  }, [openerConfig?.serviceType, openerConfig?.subServiceType, localResults.length, config?.apiKey, isInitialLoad, previousServiceType]);

  // Get service type display name
  const getServiceDisplayName = () => {
    if (!openerConfig) return 'Website Redesign';
    const { serviceType, subServiceType } = openerConfig;
    
    if (serviceType === 'website') return 'Website Redesign';
    if (serviceType === 'onboarding') {
      return subServiceType === 'apollo' ? 'Apollo.io Onboarding' : 'Instantly.ai Onboarding';
    }
    if (serviceType === 'cold_email') return 'Cold Email System';
    if (serviceType === 'partnership') return 'Web Development Partnership';
    if (serviceType === 'aeo') return 'AI Engine Optimization';
    return 'Website Redesign';
  };

  // Filter results based on search term and filter
  const filteredResults = localResults.filter(result => {
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const companyMatch = (result.company || '').toLowerCase().includes(searchLower);
      const urlMatch = ((result.url || result.website) || '').toLowerCase().includes(searchLower);
      if (!companyMatch && !urlMatch) return false;
    }
    
    // Apply score filter
    if (filter === 'high') return result.fitScore >= 7;
    if (filter === 'medium') return result.fitScore >= 5 && result.fitScore < 7;
    if (filter === 'low') return result.fitScore < 5;
    return true;
  });
  


  // Toggle website selection
  const toggleWebsiteSelection = (websiteId) => {
    setSelectedWebsites(prev => {
      if (prev.includes(websiteId)) {
        return prev.filter(id => id !== websiteId);
      } else {
        return [...prev, websiteId];
      }
    });
  };

  // Select/deselect all websites
  const toggleSelectAll = () => {
    if (selectedWebsites.length === filteredResults.length && filteredResults.length > 0) {
      setSelectedWebsites([]);
    } else {
      setSelectedWebsites(filteredResults.map(result => result.url || result.website));
    }
  };

  // Regenerate a specific opener
  const handleRegenerateOpener = async (website) => {
    // Use a more robust identifier that combines company and email since URLs might be empty
    const getUniqueId = (item) => `${item.company || ''}_${item.email || ''}`;
    const websiteId = getUniqueId(website);
    
    setRegenerating(prev => ({ ...prev, [websiteId]: true }));
    setErrorMessages(prev => ({ ...prev, [websiteId]: null }));
    
    try {
      // Check fit score threshold
      const fitScoreThreshold = config?.fitScoreThreshold || 6;
      const currentFitScore = website.fitScore || 0;
      
      if (currentFitScore < fitScoreThreshold) {
        setErrorMessages(prev => ({ ...prev, [websiteId]: `Fit score (${currentFitScore}) is below threshold (${fitScoreThreshold}). Opener generation skipped.` }));
        throw new Error(`Fit score too low: ${currentFitScore} < ${fitScoreThreshold}`);
      }
      
      // Check if API configuration is valid
      if (!config || !config.apiKey || !config.model) {
        setErrorMessages(prev => ({ ...prev, [websiteId]: 'API configuration required. Please add your API key and model in Settings.' }));
        throw new Error('API configuration required');
      }
      
      // Create a copy of the website data with all needed fields
      const websiteData = {
        ...website,
        company: website.company || extractDomain(website.url),
        fitScore: website.fitScore || 5,
        title: website.title,
        metaDescription: website.metaDescription,
        headings: website.headings,
        opportunities: website.opportunities,
        screenshot: website.screenshot // Pass screenshot URL if available
      };

      // Make the API call to regenerate the opener using the selected strategy
      // The regenerateOpener function will handle selecting the appropriate strategy based on openerConfig.strategy
      
      const result = await regenerateOpener(
        websiteData,
        config,
        { ...openerConfig, refreshData: true } // Force refresh data to get latest content
      );

      if (result.success) {
        // Update the opener and screenshot in our local state
        // Use a more robust identifier that combines company and email since URLs might be empty
        const getUniqueId = (item) => `${item.company || ''}_${item.email || ''}`;
        const websiteId = getUniqueId(website);
        
        const updatedResults = localResults.map(item => 
          getUniqueId(item) === websiteId
            ? {
                ...item, 
                personalizedOpener: result.opener,
                screenshot: result.screenshot || item.screenshot,
                serviceType: openerConfig?.serviceType,
                subServiceType: openerConfig?.subServiceType
              } 
            : item
        );
        
        setLocalResults(updatedResults);
        
        // Update the parent component's state by simulating an event
        // This is needed because we're modifying a copy of the results
        const sessionId = getCurrentSessionId();
        if (sessionId) {
          // Update the localStorage directly
          try {
            const sessionDataStr = localStorage.getItem(`session_${sessionId}_data`);
            if (sessionDataStr) {
              const sessionData = JSON.parse(sessionDataStr);
              if (sessionData.results) {
                sessionData.results = sessionData.results.map(item => 
                  getUniqueId(item) === websiteId
                    ? {
                        ...item, 
                        personalizedOpener: result.opener,
                        screenshot: result.screenshot || item.screenshot,
                        serviceType: openerConfig?.serviceType,
                        subServiceType: openerConfig?.subServiceType
                      } 
                    : item
                );
                localStorage.setItem(`session_${sessionId}_data`, JSON.stringify(sessionData));
                
                // Local state is already updated above, no need to refresh from storage
              }
            }
          } catch (error) {
            // Error updating session data
          }
        }
      } else {
        // Check if it's a scraping error but provide a more user-friendly message
        const userFriendlyError = result.error && result.error.includes('404') 
          ? 'Unable to fetch fresh website data, but regeneration will continue with existing content'
          : result.error || 'Failed to regenerate opener';
        setErrorMessages(prev => ({ ...prev, [websiteId]: userFriendlyError }));
      }
    } catch (error) {
      const userFriendlyError = error.message && error.message.includes('404')
        ? 'Unable to fetch fresh website data, but regeneration will continue with existing content'
        : error.message || 'An error occurred while regenerating the opener';
      setErrorMessages(prev => ({ ...prev, [websiteId]: userFriendlyError }));
    } finally {
      setRegenerating(prev => ({ ...prev, [websiteId]: false }));
    }
  };

  // Copy opener to clipboard
  const copyOpener = (opener, url) => {
    if (!opener || opener.startsWith('[')) {
      setErrorMessages(prev => ({ ...prev, [url]: 'No valid opener to copy' }));
      return;
    }
    
    navigator.clipboard.writeText(opener);
    setCopiedState(prev => ({ ...prev, [url]: true }));
    
    setTimeout(() => {
      setCopiedState(prev => ({ ...prev, [url]: false }));
    }, 2000);
  };

  // Download selected openers as CSV
  const downloadSelectedOpeners = () => {
    if (selectedWebsites.length === 0) {
      alert("Please select at least one website");
      return;
    }
    
    setLoading(true);
    setTimeout(() => {
      const selectedData = localResults.filter(result => selectedWebsites.includes(result.url || result.website));
      
      // Filter out websites with error messages
      const validOpeners = selectedData.filter(item => 
        item.personalizedOpener && !item.personalizedOpener.startsWith('[')
      );
      
      if (validOpeners.length === 0) {
        alert("No valid openers to download. Please generate openers first.");
        setLoading(false);
        return;
      }
      
      // Create CSV content with additional fields
      const headers = [
        'First Name',
        'Last Name',
        'Company',
        'Website',
        'Email',
        'Job Title',
        'Fit Score',
        'Service Type',
        'Personalized Opener'
      ];
      
      const csvContent = [
        headers.join(','),
        ...validOpeners.map(item => [
          `"${item.firstName || ''}"`,
          `"${item.lastName || ''}"`,
          `"${item.company || ''}"`,
          `"${item.url || ''}"`,
          `"${item.email || ''}"`,
          `"${item.jobTitle || ''}"`,
          item.fitScore || 0,
          `"${getServiceDisplayName()}"`,
          `"${(item.personalizedOpener || '').replace(/"/g, '""')}"`,
        ].join(','))
      ].join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${getServiceDisplayName().toLowerCase().replace(/\s+/g, '-')}-openers-${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setLoading(false);
    }, 1000);
  };

  // Handle sending to Instantly
  const handleSendToInstantly = () => {
    if (selectedWebsites.length === 0) {
      alert("Please select at least one website");
      return;
    }
    
    console.log('🔍 Debug selectedWebsites:', selectedWebsites);
    console.log('🔍 Debug localResults:', localResults.map(r => ({ url: r.url, website: r.website, company: r.company, email: r.email })));
    
    const selectedData = localResults.filter(result => selectedWebsites.includes(result.url || result.website));
    console.log('🔍 Debug selectedData:', selectedData.map(r => ({ url: r.url, website: r.website, company: r.company, email: r.email })));
    
    // Filter out websites with error messages or missing data
    // Note: URL is not required since we can send leads with just email and company info
    const validLeads = selectedData.filter(item => 
      item.personalizedOpener && 
      !item.personalizedOpener.startsWith('[') && 
      item.email
    );
    console.log('🔍 Debug validLeads:', validLeads.length);
    console.log('First valid lead firstName check:', validLeads[0]?.firstName);
    console.log('First valid lead full data:', validLeads[0]);
    
    if (validLeads.length === 0) {
      alert("No valid leads to send. Please ensure selected websites have valid openers and email addresses.");
      return;
    }
    
    setShowInstantlyModal(true);
  };

  // Get active settings display text
  const getSettingsText = () => {
    if (!openerConfig) return '';
    
    const { style, length, focus, strategy = '8step' } = openerConfig;
    const strategyText = strategy === '5line' ? '5-Line Template' : '8-Step Framework';
    return `${strategyText}, ${style.charAt(0).toUpperCase() + style.slice(1)}, ${length} length, ${focus} focus`;
  };

  // Check if API configuration is valid
  const isApiConfigValid = config && config.apiKey && config.model;

  // Display the service type for the openers
  const serviceType = getServiceDisplayName();

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="personalized-opener-generator"
    >
      <div style={{ background: '#f0f0f0', padding: '10px', margin: '10px 0', border: '1px solid #ccc', fontSize: '12px' }}>
        <strong>Debug Info:</strong> {debugInfo}
      </div>
      <div className="generator-header">
        <h2>{serviceType} Value-First Openers</h2>
        <p className="generator-description">
          Generate simple, easy-to-understand personalized openers for your {serviceType.toLowerCase()} outreach emails.
          {openerConfig && (
            <span className="active-settings">
              Active settings: {getSettingsText()}
            </span>
          )}
        </p>
      </div>

      {!isApiConfigValid && (
        <div className="api-warning-banner">
          <SafeIcon icon={FiAlertTriangle} className="warning-icon" />
          <p>
            <strong>API Configuration Required:</strong> Please add your API key and model in Settings to generate personalized openers.
          </p>
        </div>
      )}

      <div className="service-info-banner">
        <SafeIcon icon={FiInfo} className="info-icon" />
        <p>
          All openers use simple language a 5th grader can understand and follow a <strong>value-first approach</strong>, 
          starting with an immediate, useful tip before mentioning {serviceType}.
          {openerConfig?.serviceType === 'onboarding' && 
            ` These openers deliver easy-to-understand value first, then mention ${openerConfig.subServiceType === 'apollo' ? 'Apollo.io' : 'Instantly.ai'} as a solution.`}
          {openerConfig?.serviceType === 'cold_email' && 
            ' These openers provide simple, helpful tips first, then introduce cold email systems as a helpful tool.'}
          {openerConfig?.serviceType === 'website' && 
            ' These openers share a clear website tip first, then suggest talking about website improvements.'}
        </p>
      </div>
      
      {/* Strategy selection is now handled in OpenerStyleSettings */}

      <div className="generator-controls">
        <div className="search-filter">
          <div className="search-box">
            <SafeIcon icon={FiSearch} className="search-icon" />
            <input 
              type="text" 
              placeholder="Search websites..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="search-input"
            />
          </div>

          <div className="filter-dropdown">
            <SafeIcon icon={FiFilter} className="filter-icon" />
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)} 
              className="filter-select"
            >
              <option value="all">All Scores</option>
              <option value="high">High Score (7+)</option>
              <option value="medium">Medium Score (5-6)</option>
              <option value="low">Low Score (1-4)</option>
            </select>
          </div>
        </div>

        <div className="action-buttons">
          <button 
            className="select-all-button" 
            onClick={toggleSelectAll}
          >
            {selectedWebsites.length === filteredResults.length && filteredResults.length > 0 
              ? "Deselect All" 
              : "Select All"}
          </button>

          <button 
            className={`download-button ${selectedWebsites.length === 0 ? 'disabled' : ''}`}
            onClick={downloadSelectedOpeners} 
            disabled={selectedWebsites.length === 0 || loading}
          >
            <SafeIcon 
              icon={loading ? FiRefreshCw : FiDownload} 
              className={loading ? "spinning" : ""}
            />
            <span>
              {loading 
                ? "Generating..." 
                : `Download ${selectedWebsites.length} Opener${selectedWebsites.length !== 1 ? 's' : ''}`}
            </span>
          </button>

          <button 
            className={`instantly-button ${selectedWebsites.length === 0 ? 'disabled' : ''}`}
            onClick={handleSendToInstantly} 
            disabled={selectedWebsites.length === 0}
          >
            <SafeIcon icon={FiSend} />
            <span>Send to Instantly</span>
          </button>
        </div>
      </div>



      <div className="openers-list">
        {filteredResults.length > 0 ? (
          filteredResults
            .filter((result, index, array) => {
              // Filter out results with empty URLs and ensure unique URLs
              const url = result.url || result.website;
              return url && url.trim() && 
                     array.findIndex(r => (r.url || r.website) === url) === index;
            })
            .map((result, index) => {
            const url = result.url || result.website;
            // Use the same unique identifier approach for state management
            const getUniqueId = (item) => `${item.company || ''}_${item.email || ''}`;
            const resultId = getUniqueId(result);
            
            const isSelected = selectedWebsites.includes(url);
            const isRegenerating = regenerating[resultId];
            const isCopied = copiedState[url];
            const companyName = result.company || extractDomain(url);
            const hasError = errorMessages[resultId] || (result.personalizedOpener && result.personalizedOpener.startsWith('['));
            const errorMessage = errorMessages[resultId] || (result.personalizedOpener && result.personalizedOpener.startsWith('[') 
              ? result.personalizedOpener.replace(/[\[\]]/g, '')
              : '');

            return (
              <div 
                key={`opener-${url || 'unknown'}-${index}`} 
                className={`opener-card ${isSelected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}
              >
                <div className="card-header">
                  <div className="website-info">
                    <div className="checkbox-container">
                      <input 
                        type="checkbox" 
                        checked={isSelected} 
                        onChange={() => toggleWebsiteSelection(url)}
                        id={`select-${url}`}
                      />
                      <label 
                        htmlFor={`select-${url}`} 
                        className="checkbox-label"
                      ></label>
                    </div>
                    <div>
                      <h3 className="website-name">{companyName}</h3>
                      <a 
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="website-url"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {extractDomain(url)}
                        <SafeIcon icon={FiExternalLink} className="external-link" />
                      </a>
                      {result.email && (
                        <div className="contact-info">
                          <span className="email-info">
                            {result.firstName && result.lastName 
                              ? `${result.firstName} ${result.lastName}` 
                              : result.email}
                          </span>
                          {result.jobTitle && (
                            <span className="job-title">{result.jobTitle}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="score-badge">
                    <span className={`score-value score-${result.fitScore >= 7 ? 'high' : result.fitScore >= 5 ? 'medium' : 'low'}`}>
                      {result.fitScore || 0}
                    </span>
                  </div>
                </div>

                <div className="opener-content">
                  {(() => {
                    const fitScoreThreshold = config?.fitScoreThreshold || 6;
                    const currentFitScore = result.fitScore || 0;
                    const isBelowThreshold = currentFitScore < fitScoreThreshold;
                    
                    if (isBelowThreshold) {
                      return (
                        <div className="threshold-warning">
                          <SafeIcon icon={FiAlertTriangle} className="warning-icon" />
                          <div>
                            <p>This prospect did not pass the fit score threshold</p>
                            <p className="threshold-note">Fit score: {currentFitScore}/{fitScoreThreshold} required. Adjust threshold in Settings if needed.</p>
                          </div>
                        </div>
                      );
                    }
                    
                    if (hasError) {
                      return (
                        <div className="error-message">
                          <SafeIcon icon={FiAlertTriangle} className="error-icon" />
                          <p>{errorMessage}</p>
                        </div>
                      );
                    }
                    
                    return (
                      <p className="opener-text">
                        {result.personalizedOpener || 'No opener generated yet. Click "Regenerate" to create one.'}
                      </p>
                    );
                  })()}
                  
                  {/* Debug section to show scraped data */}
                  <DebugDataSection result={result} />
                </div>

                <div className="card-actions">
                  {(() => {
                    const fitScoreThreshold = config?.fitScoreThreshold || 6;
                    const currentFitScore = result.fitScore || 0;
                    const isBelowThreshold = currentFitScore < fitScoreThreshold;
                    
                    return (
                      <button 
                        className="action-button regenerate" 
                        onClick={() => handleRegenerateOpener(result)}
                        disabled={isRegenerating || isBelowThreshold}
                        title={isBelowThreshold 
                          ? `Fit score (${currentFitScore}) is below threshold (${fitScoreThreshold})` 
                          : `Regenerate with ${getSettingsText()} settings for ${serviceType} using ${openerConfig?.strategy === '5line' ? '5-Line Template' : '8-Step Framework'}`
                        }
                      >
                        <SafeIcon icon={FiRefreshCw} className={isRegenerating ? "spinning" : ""} />
                        <span>{isRegenerating ? "Regenerating..." : "Regenerate"}</span>
                      </button>
                    );
                  })()}

                  <button 
                    className={`action-button copy ${isCopied ? 'copied' : ''}`}
                    onClick={() => copyOpener(result.personalizedOpener || "", url)}
                    disabled={!result.personalizedOpener || hasError}
                  >
                    <SafeIcon icon={isCopied ? FiCheck : FiCopy} />
                    <span>{isCopied ? "Copied!" : "Copy"}</span>
                  </button>

                  <button 
                    className="action-button send"
                    onClick={() => {
                      setSelectedWebsites([url]);
                      handleSendToInstantly();
                    }}
                    disabled={!result.personalizedOpener || hasError || !result.email}
                    title={!result.email ? "Missing email address" : "Send to Instantly.ai"}
                  >
                    <SafeIcon icon={FiMail} />
                    <span>Send</span>
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state">
            <p>No websites match your search criteria</p>
          </div>
        )}
      </div>

      {showInstantlyModal && (
        <InstantlyModal 
          leads={localResults.filter(result => selectedWebsites.includes(result.url || result.website))}
          onClose={() => setShowInstantlyModal(false)}
        />
      )}

      <style jsx>{`
        .personalized-opener-generator {
          padding: 1.5rem;
        }
        
        .generator-header {
          margin-bottom: 1rem;
        }
        
        .generator-header h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        
        .generator-description {
          font-size: 0.875rem;
          color: var(--text-light);
        }
        
        .active-settings {
          display: inline-block;
          margin-left: 0.5rem;
          padding: 0.125rem 0.5rem;
          background-color: #f5f3ff;
          border-radius: 4px;
          color: var(--primary-color);
          font-weight: 500;
        }
        
        .api-warning-banner {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background-color: #fee2e2;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          margin-bottom: 1.5rem;
          border: 1px solid #fecaca;
        }
        
        .api-warning-banner .warning-icon {
          color: #b91c1c;
          font-size: 1.25rem;
          flex-shrink: 0;
        }
        
        .api-warning-banner p {
          font-size: 0.875rem;
          color: #b91c1c;
          margin: 0;
        }
        
        .service-info-banner {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background-color: #eff6ff;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          margin-bottom: 1.5rem;
        }
        
        .service-info-banner .info-icon {
          color: var(--blue-color);
          font-size: 1.25rem;
          flex-shrink: 0;
        }
        
        .service-info-banner p {
          font-size: 0.875rem;
          color: var(--text-color);
          margin: 0;
        }
        
        /* Approach toggle styles removed as the UI element has been replaced by the strategy selection in OpenerStyleSettings */
        
        .generator-controls {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .search-filter {
          display: flex;
          gap: 0.75rem;
          flex: 1;
        }
        
        .search-box {
          position: relative;
          flex: 1;
        }
        
        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-light);
        }
        
        .search-input {
          width: 100%;
          padding: 0.5rem 0.5rem 0.5rem 2.25rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
        }
        
        .filter-dropdown {
          position: relative;
          min-width: 150px;
        }
        
        .filter-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-light);
          pointer-events: none;
        }
        
        .filter-select {
          width: 100%;
          padding: 0.5rem 0.5rem 0.5rem 2.25rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
          background-color: white;
        }
        
        .action-buttons {
          display: flex;
          gap: 0.75rem;
        }
        
        .select-all-button {
          padding: 0.5rem 1rem;
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
          cursor: pointer;
        }
        
        .select-all-button:hover {
          background-color: #f9fafb;
        }
        
        .download-button, .instantly-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
        }
        
        .download-button {
          background-color: var(--primary-color);
        }
        
        .instantly-button {
          background-color: #0369a1;
        }
        
        .download-button:hover {
          background-color: var(--primary-dark);
        }
        
        .instantly-button:hover {
          background-color: #075985;
        }
        
        .download-button.disabled, .instantly-button.disabled {
          background-color: #e5e7eb;
          color: #9ca3af;
          cursor: not-allowed;
        }
        
        .spinning {
          animation: spin 1.5s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .openers-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1rem;
        }
        
        .opener-card {
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          padding: 1rem;
          border: 2px solid transparent;
          transition: all 0.2s ease;
        }
        
        .opener-card:hover {
          box-shadow: var(--shadow-md);
        }
        
        .opener-card.selected {
          border-color: var(--primary-color);
        }
        
        .opener-card.has-error {
          border-color: #fee2e2;
          background-color: #fef2f2;
        }
        
        .card-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1rem;
        }
        
        .website-info {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }
        
        .checkbox-container {
          position: relative;
        }
        
        input[type="checkbox"] {
          opacity: 0;
          position: absolute;
        }
        
        .checkbox-label {
          display: inline-block;
          width: 20px;
          height: 20px;
          border: 2px solid #e5e7eb;
          border-radius: 4px;
          cursor: pointer;
          position: relative;
        }
        
        input[type="checkbox"]:checked + .checkbox-label {
          background-color: var(--primary-color);
          border-color: var(--primary-color);
        }
        
        input[type="checkbox"]:checked + .checkbox-label::after {
          content: '';
          position: absolute;
          left: 6px;
          top: 2px;
          width: 6px;
          height: 10px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
        
        .website-name {
          font-size: 0.875rem;
          font-weight: 600;
          margin: 0;
        }
        
        .website-url {
          font-size: 0.75rem;
          color: var(--text-light);
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        
        .external-link {
          font-size: 0.75rem;
        }
        
        .contact-info {
          font-size: 0.75rem;
          color: var(--text-light);
          margin-top: 0.25rem;
          display: flex;
          flex-direction: column;
        }
        
        .email-info {
          color: #0369a1;
        }
        
        .job-title {
          font-style: italic;
        }
        
        .score-badge {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .score-value {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          font-size: 0.875rem;
          font-weight: 600;
        }
        
        .score-high {
          background-color: #ecfdf5;
          color: #065f46;
        }
        
        .score-medium {
          background-color: #fff7ed;
          color: #9a3412;
        }
        
        .score-low {
          background-color: #f3f4f6;
          color: #4b5563;
        }
        
        .opener-content {
          margin-bottom: 1rem;
        }
        
        .opener-text {
          font-size: 0.875rem;
          line-height: 1.5;
          color: var(--text-color);
          background-color: #f9fafb;
          padding: 1rem;
          border-radius: 8px;
          border-left: 3px solid var(--primary-light);
        }
        
        .error-message {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          background-color: #fee2e2;
          padding: 1rem;
          border-radius: 8px;
          border-left: 3px solid #ef4444;
        }
        
        .error-message .error-icon {
          color: #b91c1c;
          font-size: 1rem;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .error-message p {
          font-size: 0.875rem;
          line-height: 1.5;
          color: #b91c1c;
          margin: 0;
        }
        
        .threshold-warning {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          background-color: #fef3c7;
          padding: 1rem;
          border-radius: 8px;
          border-left: 3px solid #f59e0b;
        }
        
        .threshold-warning .warning-icon {
          color: #d97706;
          font-size: 1rem;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .threshold-warning p {
          font-size: 0.875rem;
          line-height: 1.5;
          color: #92400e;
          margin: 0 0 0.5rem 0;
        }
        
        .threshold-warning p:last-child {
          margin-bottom: 0;
        }
        
        .threshold-note {
          font-style: italic;
          opacity: 0.8;
        }
        
        .threshold-filter-notice {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background-color: #f0f9ff;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          border-left: 3px solid #0ea5e9;
          margin-bottom: 1rem;
          font-size: 0.875rem;
          color: #0c4a6e;
        }
        
        .threshold-filter-notice .info-icon {
          color: #0ea5e9;
          flex-shrink: 0;
        }
        
        .threshold-filter-notice .settings-link {
          font-weight: 500;
          text-decoration: underline;
          cursor: pointer;
        }
        
        .card-actions {
          display: flex;
          gap: 0.5rem;
        }
        
        .debug-section {
          margin-top: 1rem;
          border-top: 1px solid #e5e7eb;
          padding-top: 1rem;
        }
        
        .debug-toggle {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: none;
          border: none;
          color: #6b7280;
          font-size: 0.75rem;
          cursor: pointer;
          padding: 0.25rem 0;
          transition: color 0.2s ease;
        }
        
        .debug-toggle:hover {
          color: var(--primary-color);
        }
        
        .debug-content {
          margin-top: 0.75rem;
          background-color: #f8fafc;
          border-radius: 6px;
          padding: 0.75rem;
          border: 1px solid #e2e8f0;
        }
        
        .debug-section-item {
          margin-bottom: 1rem;
        }
        
        .debug-section-item:last-child {
          margin-bottom: 0;
        }
        
        .debug-section-item h4 {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--primary-color);
          margin: 0 0 0.5rem 0;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        
        .debug-data {
          font-size: 0.75rem;
          line-height: 1.4;
        }
        
        .debug-data p {
          margin: 0.25rem 0;
          color: #374151;
        }
        
        .debug-data strong {
          color: #1f2937;
          font-weight: 600;
        }
        
        .debug-data ul {
          margin: 0.25rem 0 0 1rem;
          padding: 0;
        }
        
        .debug-data li {
          margin: 0.125rem 0;
          color: #374151;
        }
        
        .action-button {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.5rem;
          border-radius: 8px;
          font-size: 0.75rem;
          cursor: pointer;
          justify-content: center;
        }
        
        .action-button.regenerate {
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          color: var(--text-color);
          flex: 1;
        }
        
        .action-button.copy {
          background-color: white;
          border: 1px solid #e5e7eb;
          color: var(--text-color);
          flex: 1;
        }
        
        .action-button.send {
          background-color: #eff6ff;
          border: 1px solid #bae6fd;
          color: #0369a1;
          flex: 1;
        }
        
        .action-button.copied {
          background-color: #ecfdf5;
          border-color: #a7f3d0;
          color: #065f46;
        }
        
        .action-button.regenerate:hover:not(:disabled) {
          background-color: #f3f4f6;
        }
        
        .action-button.copy:hover:not(:disabled) {
          background-color: #f9fafb;
        }
        
        .action-button.send:hover:not(:disabled) {
          background-color: #e0f2fe;
        }
        
        .action-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .empty-state {
          grid-column: 1 / -1;
          padding: 3rem;
          text-align: center;
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          color: var(--text-light);
        }
        
        /* Debug scrollable content */
        .debug-scrollable {
          background: #ffffff;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          padding: 8px;
          margin-top: 5px;
          font-size: 12px;
          line-height: 1.4;
          color: #495057;
          max-height: 200px;
          overflow-y: auto;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        
        .debug-no-data {
          color: #dc3545;
          font-style: italic;
          margin: 5px 0;
        }
        
        .debug-list {
          margin: 5px 0 0 20px;
          padding: 0;
        }
        
        .debug-list li {
          margin: 3px 0;
          font-size: 12px;
          line-height: 1.4;
        }
        
        @media (max-width: 768px) {
          .generator-controls {
            flex-direction: column;
          }
          
          .search-filter {
            flex-direction: column;
          }
          
          .action-buttons {
            justify-content: space-between;
          }
          
          .openers-list {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default PersonalizedOpenerGenerator;